import React, { useCallback, useState, useRef } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Chip,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  PhoneAndroid as AndroidIcon,
  WarningAmber as WarningIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import type { ActiveVersion } from '../../types/AppMobileTypes';

interface ApkUploadZoneProps {
  onFileSelected: (file: File) => void;
  uploadProgress: number | null;
  disabled?: boolean;
  activeVersion?: ActiveVersion | null;
}

const MAX_SIZE_MB = 300;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

function suggestNextVersion(currentVersionName: string): string {
  const parts = currentVersionName.split('.').map(p => parseInt(p, 10));
  if (parts.some(isNaN) || parts.length < 2) return '';
  parts[parts.length - 1] += 1;
  for (let i = parts.length - 1; i > 0; i--) {
    if (parts[i] >= 10) {
      parts[i] = 0;
      parts[i - 1] += 1;
    }
  }
  return parts.join('.');
}

export const ApkUploadZone: React.FC<ApkUploadZoneProps> = ({
  onFileSelected,
  uploadProgress,
  disabled = false,
  activeVersion,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showVersionDialog, setShowVersionDialog] = useState(false);
  const [declaredVersion, setDeclaredVersion] = useState('');
  const [versionError, setVersionError] = useState<string | null>(null);
  const pendingFileRef = useRef<File | null>(null);

  const isUploading = uploadProgress !== null;

  const currentVersionName = activeVersion?.versionName || null;
  const suggestedVersion = currentVersionName ? suggestNextVersion(currentVersionName) : '';

  const validateFile = useCallback((file: File): string | null => {
    if (!file.name.toLowerCase().endsWith('.apk')) {
      return 'Formato non supportato. Seleziona un file .apk';
    }
    if (file.size > MAX_SIZE_BYTES) {
      return `File troppo grande. Massimo ${MAX_SIZE_MB}MB.`;
    }
    if (file.size < 1024) {
      return 'File troppo piccolo. Probabilmente non è un APK valido.';
    }
    return null;
  }, []);

  const openVersionDialog = useCallback(() => {
    setDeclaredVersion(suggestedVersion);
    setVersionError(null);
    setShowVersionDialog(true);
  }, [suggestedVersion]);

  const handleVersionConfirmed = useCallback(() => {
    const trimmed = declaredVersion.trim();
    if (!trimmed) {
      setVersionError('Inserisci la versione che stai per caricare');
      return;
    }
    if (!/^\d+\.\d+/.test(trimmed)) {
      setVersionError('Formato non valido. Usa il formato X.Y (es. 1.5)');
      return;
    }
    if (currentVersionName && trimmed === currentVersionName) {
      setVersionError(`La versione ${trimmed} è già quella attiva. Devi incrementarla.`);
      return;
    }
    setShowVersionDialog(false);
    if (pendingFileRef.current) {
      onFileSelected(pendingFileRef.current);
      pendingFileRef.current = null;
    } else {
      document.getElementById('apk-upload-input')?.click();
    }
  }, [declaredVersion, currentVersionName, onFileSelected]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled && !isUploading) setIsDragging(true);
  }, [disabled, isUploading]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled || isUploading) return;

    const file = e.dataTransfer.files[0];
    if (!file) return;

    const fileError = validateFile(file);
    if (fileError) {
      setError(fileError);
      return;
    }
    setError(null);
    pendingFileRef.current = file;
    openVersionDialog();
  }, [disabled, isUploading, validateFile, openVersionDialog]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled || isUploading) return;
    const file = e.target.files?.[0];
    if (file) {
      const fileError = validateFile(file);
      if (fileError) {
        setError(fileError);
      } else {
        setError(null);
        onFileSelected(file);
      }
    }
    e.target.value = '';
  }, [disabled, isUploading, validateFile, onFileSelected]);

  const handleClick = useCallback(() => {
    if (!disabled && !isUploading) {
      pendingFileRef.current = null;
      openVersionDialog();
    }
  }, [disabled, isUploading, openVersionDialog]);

  return (
    <Box
      sx={{
        p: 3,
        border: '2px dashed',
        borderColor: isDragging ? '#4caf50' : error ? 'error.main' : alpha('#000', 0.12),
        borderRadius: 3,
        bgcolor: isDragging ? alpha('#4caf50', 0.04) : alpha('#000', 0.015),
        cursor: disabled || isUploading ? 'default' : 'pointer',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 160,
        opacity: disabled ? 0.5 : 1,
        '&:hover': disabled || isUploading ? {} : {
          borderColor: alpha('#4caf50', 0.5),
          bgcolor: alpha('#4caf50', 0.03),
        },
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
    >
      <input
        id="apk-upload-input"
        type="file"
        accept=".apk"
        onChange={handleFileSelect}
        onClick={(e) => e.stopPropagation()}
        style={{ display: 'none' }}
      />

      {isUploading ? (
        <Box sx={{ width: '100%', maxWidth: 360, textAlign: 'center' }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              bgcolor: alpha('#4caf50', 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <AndroidIcon sx={{ fontSize: 24, color: '#4caf50' }} />
          </Box>
          <Typography variant="body2" fontWeight={500} sx={{ mb: 1.5 }}>
            Caricamento in corso...
          </Typography>
          <LinearProgress
            variant="determinate"
            value={uploadProgress}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: alpha('#4caf50', 0.1),
              mb: 1,
              '& .MuiLinearProgress-bar': {
                bgcolor: '#4caf50',
                borderRadius: 3,
              },
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {Math.round(uploadProgress)}%
          </Typography>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '14px',
              bgcolor: isDragging ? '#4caf50' : alpha('#000', 0.05),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 1.5,
              transition: 'all 0.25s',
            }}
          >
            {isDragging ? (
              <UploadIcon sx={{ fontSize: 26, color: 'white' }} />
            ) : (
              <UploadIcon sx={{ fontSize: 26, color: 'text.secondary' }} />
            )}
          </Box>

          <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>
            {isDragging ? 'Rilascia qui il file APK' : 'Carica APK'}
          </Typography>

          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 1.5 }}>
            Trascina un file .apk qui, oppure clicca per selezionare
          </Typography>

          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.5,
              py: 0.5,
              bgcolor: 'white',
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: alpha('#000', 0.08),
              fontSize: 12,
            }}
          >
            <AndroidIcon sx={{ fontSize: 16, color: '#4caf50' }} />
            <Typography variant="caption" fontWeight={500}>.APK</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              max {MAX_SIZE_MB}MB
            </Typography>
          </Box>

          {error && (
            <Typography color="error" variant="body2" sx={{ mt: 1.5, fontSize: 13 }}>
              {error}
            </Typography>
          )}
        </>
      )}

      <Dialog
        open={showVersionDialog}
        onClose={() => { setShowVersionDialog(false); pendingFileRef.current = null; }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                bgcolor: alpha('#ff9800', 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <WarningIcon sx={{ fontSize: 22, color: '#ff9800' }} />
            </Box>
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: 17 }}>
              Conferma versione APK
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            Prima di caricare il file, conferma di aver aggiornato il <strong>versionName</strong> e il <strong>versionCode</strong> nel progetto Android.
          </Typography>

          {currentVersionName && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 2.5,
                p: 1.5,
                bgcolor: alpha('#000', 0.03),
                borderRadius: 2,
              }}
            >
              <Chip
                label={`v${currentVersionName}`}
                size="small"
                sx={{
                  fontWeight: 700,
                  bgcolor: alpha('#4caf50', 0.1),
                  color: '#2e7d32',
                  fontSize: 13,
                }}
              />
              <ArrowIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              <Typography variant="body2" color="text.secondary">
                Nuova versione?
              </Typography>
            </Box>
          )}

          <TextField
            label="Versione che stai caricando"
            value={declaredVersion}
            onChange={(e) => { setDeclaredVersion(e.target.value); setVersionError(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter') handleVersionConfirmed(); }}
            error={!!versionError}
            helperText={versionError || 'Es. 1.5, 2.0, 1.4.1'}
            fullWidth
            size="small"
            autoFocus
            placeholder="Es. 1.5"
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: 2 },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => { setShowVersionDialog(false); pendingFileRef.current = null; }}
            color="inherit"
            sx={{ borderRadius: 2 }}
          >
            Annulla
          </Button>
          <Button
            onClick={handleVersionConfirmed}
            variant="contained"
            sx={{
              borderRadius: 2,
              bgcolor: '#4caf50',
              '&:hover': { bgcolor: '#388e3c' },
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Confermo, procedi
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
